import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { locationHooks } from '../../hooks/useMasters.js';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Modal from '../../components/ui/Modal.jsx';
import ConfirmDialog from '../../components/ui/ConfirmDialog.jsx';

const schema = z.object({
  name: z.string().min(1, 'Required'),
  code: z.string().min(1, 'Required').max(10, 'Max 10 chars'),
  type: z.enum(['HEAD_OFFICE','PROCESSING_PLANT','COLD_STORAGE','OFFICE']),
  address: z.string().optional(),
  contactPerson: z.string().optional(),
  contactEmail: z.string().email('Invalid email').optional().or(z.literal('')),
  contactPhone: z.string().optional(),
});

const TYPE_LABELS = {
  HEAD_OFFICE: 'Head Office', PROCESSING_PLANT: 'Processing Plant',
  COLD_STORAGE: 'Cold Storage', OFFICE: 'Office',
};

export default function LocationsPage() {
  const { data: locations = [], isLoading } = locationHooks.useList({ includeInactive: 'true' });
  const createMut = locationHooks.useCreate();
  const updateMut = locationHooks.useUpdate();
  const deleteMut = locationHooks.useDelete();
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({ resolver: zodResolver(schema) });

  const openCreate = () => { setEditing(null); reset({}); setModalOpen(true); };
  const openEdit = (loc) => { setEditing(loc); reset(loc); setModalOpen(true); };
  const onSubmit = async (values) => {
    if (editing) await updateMut.mutateAsync({ id: editing.id, ...values });
    else await createMut.mutateAsync(values);
    setModalOpen(false); reset();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Locations</h2>
          <p className="text-sm text-gray-500">Manage factory / office locations</p>
        </div>
        <Button icon={Plus} onClick={openCreate}>Add Location</Button>
      </div>
      <Card>
        {isLoading ? <div className="p-8 text-center text-gray-400">Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>{['Code','Name','Type','Contact','Status',''].map(h => (
                  <th key={h} className="text-left px-4 py-3 font-medium text-gray-600 text-xs uppercase tracking-wide">{h}</th>
                ))}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {locations.map(loc => (
                  <tr key={loc.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono font-semibold text-blue-700">{loc.code}</td>
                    <td className="px-4 py-3 font-medium">{loc.name}</td>
                    <td className="px-4 py-3 text-gray-600">{TYPE_LABELS[loc.type]}</td>
                    <td className="px-4 py-3 text-gray-500">{loc.contactPerson || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={"badge " + (loc.isActive ? 'badge-approved' : 'badge-cancelled')}>
                        {loc.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end">
                        <button onClick={() => openEdit(loc)} className="text-gray-400 hover:text-blue-600"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => setDeleteTarget(loc)} className="text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={editing ? 'Edit Location' : 'Add Location'}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input label="Location Name" required error={errors.name?.message} {...register('name')} />
            <Input label="Code" required placeholder="e.g. KOCHI" error={errors.code?.message} {...register('code')} />
          </div>
          <Select label="Type" required error={errors.type?.message} {...register('type')}>
            <option value="">Select type…</option>
            {Object.entries(TYPE_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
          </Select>
          <Input label="Address" {...register('address')} />
          <div className="grid grid-cols-2 gap-4">
            <Input label="Contact Person" {...register('contactPerson')} />
            <Input label="Contact Phone" {...register('contactPhone')} />
          </div>
          <Input label="Contact Email" type="email" error={errors.contactEmail?.message} {...register('contactEmail')} />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="submit" loading={isSubmitting}>{editing ? 'Save Changes' : 'Create Location'}</Button>
          </div>
        </form>
      </Modal>
      <ConfirmDialog
        open={!!deleteTarget} onClose={() => setDeleteTarget(null)}
        onConfirm={async () => { await deleteMut.mutateAsync(deleteTarget.id); setDeleteTarget(null); }}
        loading={deleteMut.isPending} title="Delete Location"
        message={`Delete "${deleteTarget?.name}"? Existing gate passes are preserved (soft delete).`}
        confirmLabel="Delete"
      />
    </div>
  );
}
